import { doc, type Firestore, type Transaction } from 'firebase/firestore';

type ScheduleDay = { date: string; title?: string; slots?: unknown[]; [key: string]: unknown };
export type SavedSchedule = { days: ScheduleDay[]; [key: string]: unknown };
export interface ScheduleWrite {
  tripId: string;
  destId: string;
  userId: string;
  previous: SavedSchedule;
  next: SavedSchedule;
  sharedDay?: { date: string; ownerDestId: string };
  extra?: Record<string, unknown>;
}

function stable(value: unknown): string {
  return JSON.stringify(value, (_, item) => item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
}
function sameDay(a?: ScheduleDay, b?: ScheduleDay): boolean {
  const normalize = (day?: ScheduleDay) => ({ title: '', slots: [], ...day, date: undefined });
  return stable(normalize(a)) === stable(normalize(b));
}

/** Replay only the local delta, so delayed suggestions cannot restore an old schedule. */
export function applyScheduleEdits(latest: SavedSchedule, base: SavedSchedule, next: SavedSchedule): SavedSchedule {
  const replay = (current: any, before: any, after: any, field = ''): any => {
    if (stable(before) === stable(after)) return current;
    if (Array.isArray(before) && Array.isArray(after) && (field === 'days' || field === 'slots')) {
      const key = field === 'days' ? 'date' : 'id';
      if ([...before, ...after].every(item => typeof item?.[key] === 'string')) {
        const oldItems = new Map(before.map(item => [item[key], item]));
        const newItems = new Map(after.map(item => [item[key], item]));
        const values = (Array.isArray(current) ? current : []).filter(item => !oldItems.has(item[key]) || newItems.has(item[key]))
          .map(item => newItems.has(item[key]) ? replay(item, oldItems.get(item[key]), newItems.get(item[key])) : item);
        for (const item of after) {
          if (!oldItems.has(item[key]) && !values.some(value => value[key] === item[key])) values.push(item);
        }
        if (stable(before.map(item => item[key])) !== stable(after.map(item => item[key]))) {
          return [...after.map(item => values.find(value => value[key] === item[key])).filter(Boolean), ...values.filter(item => !newItems.has(item[key]))];
        }
        return values;
      }
    }
    if (after && typeof after === 'object' && !Array.isArray(after)) {
      const result = { ...current };
      for (const key of new Set([...Object.keys(before ?? {}), ...Object.keys(after)])) {
        if (!(key in after)) delete result[key];
        else result[key] = replay(current?.[key], before?.[key], after[key], key);
      }
      return result;
    }
    return after;
  };
  return replay(latest, base, next);
}

function mergeDays(latest: ScheduleDay[], previous: ScheduleDay[], next: ScheduleDay[]): ScheduleDay[] {
  let result = [...latest];
  for (const date of new Set([...previous, ...next].map(day => day.date))) {
    const before = previous.find(day => day.date === date);
    const after = next.find(day => day.date === date);
    const current = result.find(day => day.date === date);
    if (sameDay(before, after)) {
      if (!current && after && sameDay(undefined, after)) result.push(after);
      continue;
    }
    if (!sameDay(current, before) && !sameDay(current, after)) {
      throw new Error(`The schedule for ${date} changed on another device. Reload the saved version before editing this day again.`);
    }
    result = result.filter(day => day.date !== date);
    if (after) result.push(after);
  }
  return result.sort((a, b) => a.date.localeCompare(b.date));
}

/** Preserve unrelated days; reject same-day conflicts; commit both owners together. */
export async function writeSchedule(transaction: Transaction, db: Firestore, input: ScheduleWrite) {
  const { tripId, destId, userId, previous, next, sharedDay, extra = {} } = input;
  const ownRef = doc(db, 'trips', tripId, 'schedules', destId);
  const ownerRef = sharedDay ? doc(db, 'trips', tripId, 'schedules', sharedDay.ownerDestId) : null;
  const own = await transaction.get(ownRef);
  const owner = ownerRef ? await transaction.get(ownerRef) : null;
  const ownLatest: SavedSchedule = own.data()?.schedule ?? { days: [] };
  const ownerLatest: SavedSchedule = owner?.data()?.schedule ?? { days: [] };
  const isOwn = (day: ScheduleDay) => day.date !== sharedDay?.date;
  const metadata = Object.fromEntries(Object.entries(next)
    .filter(([key, value]) => key !== 'days' && stable(value) !== stable(previous[key])));
  const schedule = { ...ownLatest, ...metadata, days: mergeDays(
    ownLatest.days.filter(isOwn), previous.days.filter(isOwn), next.days.filter(isOwn),
  ) };
  const ownerSchedule = sharedDay ? { ...ownerLatest, days: mergeDays(
    ownerLatest.days, previous.days.filter(day => !isOwn(day)), next.days.filter(day => !isOwn(day)),
  ) } : null;
  const stamp = { updatedAt: new Date().toISOString(), updatedBy: userId };
  transaction.set(ownRef, { ...extra, schedule, ...stamp }, { merge: true });
  if (ownerRef && stable(ownerLatest) !== stable(ownerSchedule)) {
    transaction.set(ownerRef, { schedule: ownerSchedule, ...stamp }, { merge: true });
  }
  return { schedule, owner: ownerSchedule };
}

export interface LockedBookingSlot {
  id: string; type: string; title: string; activityId: string;
  start: string; durationMin: number; locked: true;
}

/**
 * Read the destination's schedule doc inside an already-open transaction
 * (call before any writes in that transaction, same read-before-write rule
 * as every other Firestore transaction) and return the day's current slots
 * plus whether `slot` still needs to be appended — idempotent against a
 * retried import re-adding the same booking.
 */
export async function readScheduleDayForSlot(
  transaction: Transaction, db: Firestore, tripId: string, destId: string, date: string, slotId: string,
): Promise<{ ref: ReturnType<typeof doc>; schedule: SavedSchedule; alreadyPresent: boolean }> {
  const ref = doc(db, 'trips', tripId, 'schedules', destId);
  const snap = await transaction.get(ref);
  const schedule: SavedSchedule = snap.data()?.schedule ?? { days: [] };
  const day = schedule.days.find(d => d.date === date);
  const alreadyPresent = !!(day?.slots as { id?: string }[] | undefined)?.some(s => s.id === slotId);
  return { ref, schedule, alreadyPresent };
}

/** Append one locked slot to a single day, preserving every other day/slot untouched. */
export function withAppendedSlot(schedule: SavedSchedule, date: string, slot: LockedBookingSlot): SavedSchedule {
  const day = schedule.days.find(d => d.date === date);
  const days = day
    ? schedule.days.map(d => d.date === date ? { ...d, slots: [...(d.slots as unknown[] ?? []), slot] } : d)
    : [...schedule.days, { date, title: '', slots: [slot] }].sort((a, b) => a.date.localeCompare(b.date));
  return { ...schedule, days };
}
